import useCurrentUser from '@/hooks/useCurrentUser';
import React, { createContext, useCallback, useContext, useState } from 'react';

interface DownloadAppModalContextValue {
  isOpen: boolean;
  content: React.ReactNode | null;
  setContent: (content: React.ReactNode | null) => void;
  requireTrading: () => boolean;
  openModal: () => void;
  closeModal: () => void;
}

const DownloadAppModalContext = createContext<DownloadAppModalContextValue | null>(null);

export function DownloadAppModalProvider({ children }: { children: React.ReactNode }) {
  const { data: user } = useCurrentUser();
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<React.ReactNode | null>(null);

  const requireTrading = useCallback((): boolean => {
    if (user?.canShowTradingFeatures) {
      return true;
    }
    setIsOpen(true);
    return false;
  }, [user?.canShowTradingFeatures]);

  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setContent(null);
  }, []);

  return (
    <DownloadAppModalContext.Provider
      value={{
        isOpen,
        content,
        setContent,
        requireTrading,
        openModal,
        closeModal,
      }}
    >
      {children}
    </DownloadAppModalContext.Provider>
  );
}

export function useDownloadAppModal() {
  const context = useContext(DownloadAppModalContext);
  if (!context) {
    throw new Error('useDownloadAppModal must be used within a DownloadAppModalProvider');
  }
  return context;
}
